import { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import JsBarcode from "jsbarcode";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Barcode, Printer, Search, Package, RefreshCw, Loader2 } from "lucide-react";
import { API } from "@/App";

export default function BarcodeGenerator({ user }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const barcodeRef = useRef(null);
  const printRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProduct && (selectedProduct.barcode || selectedProduct.internal_barcode)) {
      renderBarcode();
    }
  }, [selectedProduct]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/products`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!search) return;

    try {
      const response = await fetch(`${API}/products/barcode/${search}`, {
        credentials: "include",
      });
      if (response.ok) {
        const product = await response.json();
        setSelectedProduct(product);
        toast.success(`Found: ${product.name}`);
      } else {
        toast.error("Product not found");
      }
    } catch (error) {
      toast.error("Error searching product");
    }
  };

  const handleGenerateBarcode = async () => {
    if (!selectedProduct) return;

    setGenerating(true);
    try {
      const response = await fetch(`${API}/products/${selectedProduct.product_id}/generate-barcode`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedProduct((prev) => ({
          ...prev,
          internal_barcode: data.internal_barcode,
        }));
        toast.success(`Barcode generated: ${data.internal_barcode}`);
        fetchProducts();
      } else {
        toast.error("Failed to generate barcode");
      }
    } catch (error) {
      toast.error("Error generating barcode");
    } finally {
      setGenerating(false);
    }
  };

  const renderBarcode = () => {
    if (!barcodeRef.current || !selectedProduct) return;

    const code = selectedProduct.barcode || selectedProduct.internal_barcode;
    if (!code) return;

    try {
      JsBarcode(barcodeRef.current, code, {
        format: "CODE128",
        width: 2,
        height: 60,
        displayValue: true,
        fontSize: 14,
        font: "JetBrains Mono",
        textMargin: 8,
        margin: 10,
      });
    } catch (error) {
      console.error("Barcode render error:", error);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Barcode-${selectedProduct?.sku}`,
    onAfterPrint: () => toast.success("Print job sent"),
  });

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.sku.toLowerCase().includes(search.toLowerCase())
  );

  const barcodeValue = selectedProduct?.barcode || selectedProduct?.internal_barcode;

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="barcode-generator-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Barcode Generator
          </h1>
          <p className="text-muted-foreground">
            Generate and print barcode labels for products
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Product Selection */}
          <Card className="rounded-sm border">
            <CardHeader>
              <CardTitle className="text-base font-heading">Select Product</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="flex gap-2">
                <Input
                  placeholder="Search by name, SKU, or barcode..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="rounded-sm"
                  data-testid="barcode-search-input"
                />
                <Button variant="secondary" onClick={handleSearch} className="rounded-sm">
                  <Search className="h-4 w-4" />
                </Button>
              </div>

              {/* Product Dropdown */}
              <div className="space-y-2">
                <Label>Or select from list</Label>
                <Select
                  value={selectedProduct?.product_id || ""}
                  onValueChange={(v) => {
                    const product = products.find((p) => p.product_id === v);
                    setSelectedProduct(product);
                  }}
                >
                  <SelectTrigger className="rounded-sm" data-testid="product-select">
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredProducts.map((p) => (
                      <SelectItem key={p.product_id} value={p.product_id}>
                        <div className="flex items-center gap-2">
                          <span>{p.name}</span>
                          <span className="text-xs text-muted-foreground">({p.sku})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selected Product Info */}
              {selectedProduct && (
                <div className="p-4 rounded-sm bg-muted">
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-12 rounded-sm bg-background flex items-center justify-center border">
                      <Package className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{selectedProduct.name}</p>
                      <p className="text-sm text-muted-foreground font-data">
                        SKU: {selectedProduct.sku}
                      </p>
                      {selectedProduct.barcode && (
                        <p className="text-sm text-muted-foreground font-data">
                          Barcode: {selectedProduct.barcode}
                        </p>
                      )}
                      {selectedProduct.internal_barcode && (
                        <p className="text-sm text-muted-foreground font-data">
                          Internal: {selectedProduct.internal_barcode}
                        </p>
                      )}
                    </div>
                  </div>

                  {!barcodeValue && (
                    <Button
                      onClick={handleGenerateBarcode}
                      disabled={generating}
                      className="rounded-sm w-full mt-4"
                      data-testid="generate-barcode-btn"
                    >
                      {generating ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Generate Internal Barcode
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Barcode Preview & Print */}
          <Card className="rounded-sm border">
            <CardHeader>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Barcode className="h-5 w-5" />
                Barcode Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedProduct && barcodeValue ? (
                <>
                  {/* Preview */}
                  <div className="p-6 bg-white rounded-sm border flex flex-col items-center">
                    <svg ref={barcodeRef}></svg>
                    <p className="mt-2 text-black font-medium text-center">
                      {selectedProduct.name}
                    </p>
                    <p className="text-gray-600 text-sm font-data">{selectedProduct.sku}</p>
                  </div>

                  {/* Print Quantity */}
                  <div className="space-y-2">
                    <Label>Print Quantity</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={quantity}
                        onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                        className="rounded-sm font-data w-24"
                        data-testid="print-quantity-input"
                      />
                      <Button onClick={handlePrint} className="rounded-sm flex-1" data-testid="print-btn">
                        <Printer className="h-4 w-4 mr-2" />
                        Print Labels
                      </Button>
                    </div>
                  </div>

                  {/* Hidden Print Area */}
                  <div style={{ display: "none" }}>
                    <div ref={printRef} className="p-4">
                      {Array.from({ length: quantity }).map((_, index) => (
                        <div
                          key={index}
                          className="barcode-label mb-4"
                          style={{
                            pageBreakAfter: index < quantity - 1 ? "always" : "auto",
                          }}
                        >
                          <div className="text-center">
                            <svg
                              ref={(el) => {
                                if (el) {
                                  try {
                                    JsBarcode(el, barcodeValue, {
                                      format: "CODE128",
                                      width: 1.5,
                                      height: 40,
                                      displayValue: true,
                                      fontSize: 10,
                                      margin: 5,
                                    });
                                  } catch (e) {}
                                }
                              }}
                            ></svg>
                            <p className="text-sm font-medium mt-1">{selectedProduct.name}</p>
                            <p className="text-xs text-gray-600">{selectedProduct.sku}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="h-48 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Barcode className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select a product to preview barcode</p>
                    <p className="text-sm mt-1">
                      Products without barcodes can have one generated
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
